// MatchingScreen — Hybrid TaskRabbit-killer UX:
//   1. Loading sequence ("Verfügbare Anbieter werden geprüft...", etc.)
//   2. Results: best provider as "Beste Wahl jetzt" + 2 alternatives
//   3. Live trust summary (contacted / responded / status)
//   4. Primary "Jetzt bestätigen", Secondary "Andere Anbieter ansehen"
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle, Star, MapPin, Clock, Shield, Sparkles, RefreshCw } from "lucide-react";
import { apiUrl } from "../lib/api";

const LOADING_STEPS = [
  "Verfügbare Anbieter werden geprüft...",
  "Entfernung, Verfügbarkeit und Antwortzeit werden verglichen...",
  "Die besten Anbieter werden kontaktiert...",
];

function timeAgoDe(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `vor ${sec} Sek.`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `vor ${hr} Std.`;
  return `vor ${Math.floor(hr / 24)} T.`;
}

function ProviderCard({ provider, onConfirm, isBest, isDemo, disabled }) {
  const reasons = provider.reasons || [];
  const hasRealRating = provider.review_count > 0 && provider.rating > 0;
  const respText =
    typeof provider.avg_response_time_minutes === "number" && provider.avg_response_time_minutes >= 0
      ? `Antwortet meist in ${Math.max(1, Math.round(provider.avg_response_time_minutes))} Min.`
      : null;
  const lastActiveText = provider.last_active_at
    ? `Zuletzt aktiv ${timeAgoDe(provider.last_active_at)}`
    : null;

  return (
    <div
      className={`relative rounded-2xl border p-4 mb-3 ${
        isBest
          ? "bg-gradient-to-br from-blue-50 to-emerald-50 border-blue-300 shadow-md"
          : "bg-white border-gray-200"
      }`}
      data-testid={isBest ? "best-match-card" : "match-card"}
    >
      {isBest && (
        <span
          className="absolute -top-3 left-4 inline-flex items-center gap-1 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow"
          data-testid="best-match-badge"
        >
          <Sparkles className="h-3 w-3" /> Beste Wahl jetzt
        </span>
      )}
      {isDemo && (
        <span className="absolute top-3 right-3 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-1 rounded-full">
          Demo Anbieter
        </span>
      )}

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">
          {(provider.name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{provider.name}</h3>
            {provider.is_verified && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                <Shield className="h-3 w-3" /> Verifiziert
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
            {provider.postal_code && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {provider.postal_code} {provider.city}
              </span>
            )}
            {lastActiveText && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {lastActiveText}
              </span>
            )}
            {respText && (
              <span className="inline-flex items-center gap-1 text-blue-700">
                <Clock className="h-3 w-3" /> {respText}
              </span>
            )}
            {hasRealRating && (
              <span className="inline-flex items-center gap-1 text-amber-600">
                <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                {provider.rating.toFixed(1)} ({provider.review_count})
              </span>
            )}
          </div>

          {reasons.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="match-reasons">
              {reasons.map((r, i) => (
                <li
                  key={i}
                  className="text-[11px] bg-blue-50 text-blue-800 px-2 py-0.5 rounded-md"
                >
                  {r}
                </li>
              ))}
            </ul>
          )}

          {!hasRealRating && !respText && (
            <p className="mt-2 text-[11px] text-gray-500 italic">
              Neue Plattform – erste Nutzer profitieren am meisten.
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        {isDemo ? (
          <button
            disabled
            className="w-full h-11 rounded-xl bg-gray-100 text-gray-400 text-sm font-medium cursor-not-allowed"
          >
            Nur Vorschau
          </button>
        ) : (
          <button
            onClick={() => onConfirm(provider)}
            disabled={disabled}
            className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-all ${
              isBest
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow"
                : "bg-white border border-blue-300 text-blue-700 hover:bg-blue-50"
            }`}
            data-testid={isBest ? "confirm-best-btn" : "confirm-other-btn"}
          >
            {isBest ? "Jetzt bestätigen" : "Diesen wählen"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MatchingScreen({ requestId, initialMatches, onReset }) {
  const [phase, setPhase] = useState("loading"); // loading | results
  const [loadingStep, setLoadingStep] = useState(0);
  const [showOthers, setShowOthers] = useState(false);
  const [matches, setMatches] = useState(initialMatches || []);
  const [trust, setTrust] = useState({
    contacted: (initialMatches || []).length,
    responded: 0,
    accepted: 0,
    live_state: "waiting",
    live_message: `${(initialMatches || []).length} Anbieter wurden kontaktiert. Antworten werden erwartet.`,
  });
  const [rematching, setRematching] = useState(false);
  const trustPollRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Loading sequence (3s total)
  useEffect(() => {
    if (phase !== "loading") return undefined;
    const id1 = setTimeout(() => setLoadingStep(1), 900);
    const id2 = setTimeout(() => setLoadingStep(2), 1800);
    const id3 = setTimeout(() => setPhase("results"), 2800);
    return () => {
      clearTimeout(id1);
      clearTimeout(id2);
      clearTimeout(id3);
    };
  }, [phase]);

  // Live trust polling once results are visible
  useEffect(() => {
    if (phase !== "results" || !requestId) return undefined;
    const fetchTrust = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.get(apiUrl(`/requests/${requestId}/trust-summary`), { headers });
        if (r.data?.ok) setTrust(r.data);
      } catch (_) {
        /* silent */
      }
    };
    fetchTrust();
    trustPollRef.current = setInterval(fetchTrust, 15000);
    return () => clearInterval(trustPollRef.current);
  }, [phase, requestId, token]);

  const refreshMatches = async () => {
    if (!requestId || !token) return;
    try {
      const r = await axios.get(apiUrl(`/requests/${requestId}/matches`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.ok) setMatches(r.data.matches || []);
    } catch (_) {
      /* silent */
    }
  };

  const handleRematch = async () => {
    if (!requestId || !token) return;
    setRematching(true);
    try {
      const r = await axios.post(
        apiUrl(`/requests/${requestId}/rematch`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.ok && r.data.matches?.length) {
        await refreshMatches();
      }
    } catch (_) {
      /* silent */
    } finally {
      setRematching(false);
    }
  };

  const handleConfirm = (provider) => {
    // Customer-side confirmation is currently surfaced as a pending state —
    // the backend acceptance still requires the worker to accept the lead.
    // We simply tell the customer their preference is recorded.
    alert(
      `Deine Auswahl: ${provider.name}\n\n` +
        "Wir teilen dem Anbieter mit, dass du ihn bevorzugst. " +
        "Du erhältst eine Benachrichtigung, sobald er bestätigt."
    );
  };

  // ---- Render: Loading ----
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 pb-20 md:pb-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 text-center" data-testid="matching-loading">
          <div className="w-20 h-20 mx-auto mb-5 relative">
            <div className="absolute inset-0 rounded-full border-4 border-blue-200" />
            <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Wir finden den passenden Anbieter für dich
          </h2>
          <p className="text-gray-600 text-sm min-h-[3rem]" data-testid="loading-step-text">
            {LOADING_STEPS[loadingStep]}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            {LOADING_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  i <= loadingStep ? "bg-blue-600" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- Render: Results ----
  const best = matches.find((m) => m.is_best_match) || matches[0];
  const others = matches.filter((m) => m !== best);
  const noMatch = matches.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-12">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        {/* Live Trust Banner */}
        <div
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4"
          data-testid="trust-banner"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{trust.live_message}</p>
                <p className="text-xs text-gray-500">
                  {trust.contacted} Anbieter kontaktiert · {trust.responded} hat
                  {trust.responded === 1 ? "" : "ten"} geantwortet
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Result Header */}
        {!noMatch && (
          <div className="mb-3">
            <h1 className="text-xl font-bold text-gray-900">
              {best ? "Beste Wahl jetzt gefunden" : "Anfrage gesendet"}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Diese Anbieter wurden ausgewählt, weil sie aktuell verfügbar, in
              deiner Nähe und schnell in der Antwort sind.
            </p>
          </div>
        )}

        {noMatch ? (
          <div
            className="bg-white rounded-2xl border border-gray-200 p-8 text-center"
            data-testid="match-empty-state"
          >
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Wir bauen das Netzwerk auf
            </h2>
            <p className="text-sm text-gray-600">
              In deiner Region sind noch nicht genug Anbieter aktiv. Neue
              Anbieter kommen bald dazu.
            </p>
          </div>
        ) : (
          <>
            {best && (
              <ProviderCard
                provider={best}
                onConfirm={handleConfirm}
                isBest
              />
            )}

            {others.length > 0 && !showOthers && (
              <button
                onClick={() => setShowOthers(true)}
                className="w-full mt-2 mb-3 h-12 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
                data-testid="show-other-providers-btn"
              >
                Andere Anbieter ansehen ({others.length})
              </button>
            )}

            {showOthers &&
              others.map((p) => (
                <ProviderCard
                  key={p.provider_id}
                  provider={p}
                  onConfirm={handleConfirm}
                />
              ))}
          </>
        )}

        {/* Re-match action */}
        {trust.live_state !== "accepted" && (
          <button
            onClick={handleRematch}
            disabled={rematching}
            className="w-full mt-4 h-12 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 inline-flex items-center justify-center gap-2"
            data-testid="rematch-btn"
          >
            <RefreshCw className={`h-4 w-4 ${rematching ? "animate-spin" : ""}`} />
            {rematching
              ? "Wir suchen weiter..."
              : "Weitere Anbieter suchen (ohne die obigen)"}
          </button>
        )}

        <p className="mt-4 text-center text-xs text-gray-500 px-4">
          Wir finden zuerst den besten Anbieter für dich. Du kannst danach
          jederzeit andere Optionen ansehen.
        </p>

        <button
          onClick={onReset}
          className="block mx-auto mt-6 text-sm text-blue-600 hover:underline"
        >
          Neue Anfrage erstellen
        </button>
      </div>
    </div>
  );
}
